import { MutationCache, QueryClient } from "@tanstack/react-query"

/*
  A job's activity is a ledger of what happened to its candidates — comments,
  emails, events, adds, stage moves — written from dozens of places across the
  app, and nothing refreshed it, so Today showed a new comment only after a
  reload. Rather than thread the refresh through every one of those writes,
  any mutation that succeeds marks every job's activity stale. Only an
  activity list on screen refetches; the rest refresh when next shown.

  Not awaited: the cache waits on what onSuccess returns, and every save in
  the app would otherwise wait on the activity refetch before resolving.
*/
export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      void invalidateJobActivityQueries(queryClient)
    },
  }),
})

const SEARCH_CACHE_MS = 24 * 60 * 60 * 1000

queryClient.setQueryDefaults(["/search/candidates"], {
  staleTime: SEARCH_CACHE_MS,
  gcTime: SEARCH_CACHE_MS,
})

queryClient.setQueryDefaults(["/search/results"], {
  staleTime: SEARCH_CACHE_MS,
  gcTime: SEARCH_CACHE_MS,
})

/*
  Every cached list a task can appear in: the task lists under "/tasks" (the
  Ops board, a candidate's tasks) and each job's Today tab, which reads
  "/jobs/{jobId}/pipeline/tasks". A task created or edited anywhere has to
  refresh all of them — Today stays mounted behind the other job tabs, so a
  task added from Pipeline used to leave it showing the old list.
*/
const JOB_TASKS_KEY = /^\/jobs\/[^/]+\/pipeline\/tasks$/

export function invalidateTaskQueries(client: QueryClient = queryClient) {
  return client.invalidateQueries({
    predicate: ({ queryKey: [root] }) =>
      root === "/tasks" || (typeof root === "string" && JOB_TASKS_KEY.test(root)),
  })
}

/*
  Every job's activity, "/jobs/{jobId}/activities". For writes that do not go
  through a mutation — Robin's tools run on the server — the caller refreshes
  it directly.
*/
const JOB_ACTIVITY_KEY = /^\/jobs\/[^/]+\/activities$/

const isJobActivity = ({ queryKey: [root] }: { queryKey: readonly unknown[] }) =>
  typeof root === "string" && JOB_ACTIVITY_KEY.test(root)

/*
  Cancelled first: a list still on its first load is fetching with no data,
  and an invalidation then joins that request rather than starting another —
  so a read that began before the write, and lands after it, would clear the
  stale mark while missing the new entry. Cancelling (which reverts, never
  errors) makes the refetch a fresh read.
*/
export async function invalidateJobActivityQueries(client: QueryClient = queryClient) {
  await client.cancelQueries({ predicate: isJobActivity })
  return client.invalidateQueries({ predicate: isJobActivity })
}
